import React, { useMemo, createContext, useContext, useEffect } from 'react';
import { useGetCountryControllerQuery } from 'services/talentoAPI/hiringAPI';

const externalCodeContext = createContext({
  countryOfResidence: '',
  departmentOfResidence: '',
  cityOfResidence: '',
  countryOfBirth: '',
  departmentOfBirth: '',
  countryBirthCitiesCode: '',
  countryBirthStatesCode: '',
  countryStatesCode: '',
  countryCitiesCode: '',
  setCountryOfResidence: (country: string) => {},
  setDepartmentOfResidence: (department: string) => {},
  setCityOfResidence: (city: string) => {},
  setCountryOfBirth: (country: string) => {},
  setDepartmentOfBirth: (department: string) => {},
  setCountryBirthCitiesCode: (code: string) => {},
  setCountryBirthStatesCode: (code: string) => {},
  setCountryStatesCode: (code: string) => {},
  setCountryCitiesCode: (code: string) => {},
});

export function useExternalCodeContext() {
  return useContext(externalCodeContext);
}

export function ExternalCodesProvider({
  children,
}: Readonly<{
  children: React.ReactNode;
}>) {
  const [countryOfResidence, setCountryOfResidence] = React.useState('');
  const [departmentOfResidence, setDepartmentOfResidence] = React.useState('');
  const [cityOfResidence, setCityOfResidence] = React.useState('');
  const [countryOfBirth, setCountryOfBirth] = React.useState('');
  const [departmentOfBirth, setDepartmentOfBirth] = React.useState('');
  const [countryBirthCitiesCode, setCountryBirthCitiesCode] = React.useState('');
  
  const [countryStatesCode, setCountryStatesCode] = React.useState('');
  const [countryCitiesCode, setCountryCitiesCode] = React.useState('');
  const [countryBirthStatesCode, setCountryBirthStatesCode] = React.useState('');
  
  const {
    refetch,
    data,
    isLoading,
    isError: isErrorCountryController,
    isFetching,
  } = useGetCountryControllerQuery(countryOfResidence);

  const {
    refetch: refetchCountryBirth,
    data: dataCountryBirth,
    isLoading: isLoadingCountryBirth,
    isError: isErrorCountryBirthController,
    isFetching: isFetchingCountryBirth,
  } = useGetCountryControllerQuery(countryOfBirth);
  
  

  useEffect(() => {
    if (!(isLoading || isFetching)) {
      let departmentListCode = '';
      let cityListCode = '';

      if (!isErrorCountryController) {
        departmentListCode = data?.departmentList ?? '';
        cityListCode = data?.cityList ?? '';
      }

      setCountryStatesCode(departmentListCode);
      setCountryCitiesCode(cityListCode);
    }
  }, [data, isLoading, isErrorCountryController, isFetching]);
  
    useEffect(() => {
      refetch();
      // eslint-disable-next-line
    }, [countryOfResidence]);
  
    useEffect(() => {
      if (!(isLoadingCountryBirth || isFetchingCountryBirth)) {
        let departmentListCode = '';
        let cityListCode = '';
  
        if (!isErrorCountryBirthController) {
          departmentListCode = dataCountryBirth?.departmentList ?? '';
          cityListCode = dataCountryBirth?.cityList ?? '';
        }
  
        setCountryBirthStatesCode(departmentListCode);
        setCountryBirthCitiesCode(cityListCode);
      }
    }, [
      dataCountryBirth,
      isLoadingCountryBirth,
      isErrorCountryBirthController,
      isFetchingCountryBirth,
    ]);
  
    useEffect(() => {
      refetchCountryBirth();
      // eslint-disable-next-line
    }, [countryOfBirth]);

  const value = useMemo(
    () => ({
      countryOfResidence,
      departmentOfResidence,
      cityOfResidence,
      countryOfBirth,
      departmentOfBirth,
      countryBirthCitiesCode,
      countryBirthStatesCode,
      countryStatesCode,
      countryCitiesCode,
      setCountryOfResidence,
      setDepartmentOfResidence,
      setCityOfResidence,
      setCountryOfBirth,
      setDepartmentOfBirth,
      setCountryBirthCitiesCode,
      setCountryBirthStatesCode,
      setCountryStatesCode,
      setCountryCitiesCode,
    }),
    [
      countryOfResidence,
      departmentOfResidence,
      cityOfResidence,
      countryOfBirth,
      departmentOfBirth,
      countryBirthCitiesCode,
      countryBirthStatesCode,
      countryStatesCode,
      countryCitiesCode,
    ]
  );

  return (
    <externalCodeContext.Provider value={value}>
      {children}
    </externalCodeContext.Provider>
  );
}
